import React, { useState } from 'react';
import TitleHelmet from 'components/TitleHelmet';
import QuizEngine, { QuizData } from 'components/QuizEngine';

const concentrationLabelQuizData: QuizData = {
  title: 'Test Your Label Reading Skills',
  description:
    'Practice identifying the medication concentration from various example labels. This will help you accurately use the dosage calculators.',
  analyticsName: 'Label Reading Quiz',
  scoreMessages: {
    perfect: ['Perfect!', "You're ready to read any medication label that comes your way."],
    good: ['Good work!', "There's still room for improvement. Review the guide and try again to master label reading."],
    needsWork: [
      'Keep Studying!',
      "These formats can be tricky! Take another look at the guide above and try again - it's important to get this right.",
    ],
  },
  questions: [
    {
      id: 1,
      labelText: `COMPOUNDED MEDICATION
Tirzepatide 15 mg/mL
For subcutaneous injection only
Qty: 3mL       Rx#: 98765
Exp: 04/15/2025
Store refrigerated (2-8°C/36-46°F)
Different Pharmacy, LLC
789 Oak St, City, ST 12345
(555) 555-1234`,
      question: 'What is the concentration of tirzepatide in this vial?',
      options: ['5 mg/mL', '10 mg/mL', '15 mg/mL', '20 mg/mL'],
      correctAnswer: '15 mg/mL',
      explanation:
        'The concentration is clearly stated as 15 mg/mL on the label. This is the standard format showing concentration directly in mg/mL.',
    },
    {
      id: 2,
      labelText: `COMPOUNDED MEDICATION
Tirzepatide 7.5mg/0.5mL
Total Volume: 3mL
For subcutaneous injection only
Rx#: 45678       Lot#: TZ0324A
Exp: 05/01/2025
Keep Refrigerated
Another Pharmacy Compounding
123 Pine Ave, City, ST 12345
(555) 555-9876`,
      question: 'If the vial shows 7.5mg/0.5mL, what is the actual concentration?',
      options: ['7.5 mg/mL', '15 mg/mL', '20 mg/mL', '25 mg/mL'],
      correctAnswer: '15 mg/mL',
      explanation:
        'Since 0.5mL is half a milliliter, and there are 7.5mg in that half mL, there must be twice as much (15mg) in a whole milliliter. So double 7.5mg to get the concentration of 15 mg/mL.',
    },
    {
      id: 3,
      labelText: `COMPOUNDED MEDICATION
Tirzepatide/B12 12/0.5 mg/mL
Volume: 4mL       Rx#: 24680
Exp: 04/01/2025   Lot#: TZB0324C
Store in refrigerator
Discard after: 28 days from first use
Third Pharmacy Solutions
456 Maple St, City, ST 12345
(555) 555-4321`,
      question: 'In a vial containing Tirzepatide/B12 12/0.5 mg/mL, what is the tirzepatide concentration?',
      options: ['0.5 mg/mL', '12 mg/mL', '12.5 mg/mL', '15 mg/mL'],
      correctAnswer: '12 mg/mL',
      explanation:
        "When additives are present, look for the tirzepatide concentration only. Here it's 12 mg/mL, with B12 being an additional ingredient that is not relevant for dosing calculations.",
    },
    {
      id: 4,
      labelText: `COMPOUNDED MEDICATION
Tirzepatide/Glycine cmpd. - 8.5MG - 5MG / ML
Volume: 3mL       Rx#: 34567
Exp: 05/15/2025   Lot#: TZG0324D
Store in refrigerator
Discard after: 28 days from first use
Valley Compounding Pharmacy
567 Pine St, City, ST 12345
(555) 555-8765`,
      question: 'In this vial where dashes separate the components, what is the tirzepatide concentration?',
      options: ['5 mg/mL', '8.5 mg/mL', '13.5 mg/mL', '15 mg/mL'],
      correctAnswer: '8.5 mg/mL',
      explanation:
        'When components are separated by dashes, the concentrations still match the order of ingredients. Since tirzepatide is first, use the first number: 8.5 mg/mL.',
    },
    {
      id: 5,
      labelText: `COMPOUNDED MEDICATION
Tirzepatide-B12 Compound 10mg-500mcg/mL
Total Volume: 2.5mL
Rx#: 89012       Lot#: TZB0324E
Exp: 04/30/2025
Keep Refrigerated
Summit Pharmacy Solutions
890 Oak Ave, City, ST 12345
(555) 555-2345`,
      question: 'What is the tirzepatide concentration in this vial that uses mcg for the additive?',
      options: ['500 mcg/mL', '10 mg/mL', '10.5 mg/mL', '15 mg/mL'],
      correctAnswer: '10 mg/mL',
      explanation:
        "Don\'t be confused by the different units (mg vs mcg) - just focus on the tirzepatide amount. Since tirzepatide is listed first, use the first number: 10 mg/mL.",
    },
    {
      id: 6,
      labelText: `COMPOUNDED MEDICATION
Tirzepatide 5mg/0.5mL
Total Volume: 4mL
For subcutaneous injection only
Rx#: 78901       Lot#: TZ0324F
Exp: 05/30/2025
Keep Refrigerated
Precision Compounding
234 Maple Ave, City, ST 12345
(555) 555-6789`,
      question: 'What is the concentration when the label shows 5mg/0.5mL?',
      options: ['5 mg/mL', '10 mg/mL', '15 mg/mL', '20 mg/mL'],
      correctAnswer: '10 mg/mL',
      explanation:
        'The label shows 5mg in 0.5mL (half a milliliter). To find the concentration per whole milliliter, double the first number: 5mg × 2 = 10 mg/mL. Remember, when you see an amount per 0.5mL, double it to get the concentration per mL.',
    },
  ],
};

const QuizConcentrationLabel: React.FC = () => {
  const [showIntroModal, setShowIntroModal] = useState(true);

  const handleStartQuiz = () => {
    setShowIntroModal(false);
  };

  const handleIntroModalOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleStartQuiz();
    }
  };

  return (
    <div className="page-container quiz-concentration-label-page">
      <TitleHelmet title="Concentration Label Quiz | Fat Scientist" />
      <section className="content-section">
        <h2>{concentrationLabelQuizData.title}</h2>
        <QuizEngine quizData={concentrationLabelQuizData} />
      </section>

      {/* Intro Modal */}
      {showIntroModal && (
        <div className="intro-modal-overlay" onClick={handleIntroModalOverlayClick}>
          <div className="intro-modal">
            <h3>{concentrationLabelQuizData.title}</h3>
            <p>{concentrationLabelQuizData.description}</p>
            <button onClick={handleStartQuiz} className="intro-modal-button">
              Let's go!
            </button>
          </div>
        </div>
      )}

      <style>{`
        .quiz-concentration-label-page {
          padding: 2rem 0;
        }
        .content-section {
          max-width: 800px;
          margin: 0 auto 2rem auto;
          padding: 0 1rem;
        }
        
        .intro-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1001;
          padding: 1rem;
        }
        
        .intro-modal {
          background-color: white;
          border-radius: 12px;
          padding: 3rem 2rem;
          max-width: 500px;
          width: 100%;
          text-align: center;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          animation: introModalSlideIn 0.4s ease-out;
        }
        
        @keyframes introModalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        .intro-modal h3 {
          color: #0066cc;
          font-size: 1.8rem;
          margin-bottom: 1.5rem;
        }
        
        .intro-modal p {
          font-size: 1.1rem;
          line-height: 1.6;
          color: #333;
          margin-bottom: 2rem;
        }
        
        .intro-modal-button {
          background-color: #0066cc;
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 1.2rem;
          font-weight: 600;
          width: 100%;
          max-width: 200px;
          transition: background-color 0.2s ease;
        }
        
        .intro-modal-button:hover {
          background-color: #0052a3;
        }
      `}</style>
    </div>
  );
};

export default QuizConcentrationLabel;
